import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, MapPin, ShieldCheck } from "lucide-react";
import { COMPANY, SERVICE_AREAS } from "@/data/siteData";

const socials = [
  { href: COMPANY.socials.facebook, Icon: Facebook, label: "Facebook", id: "footer-facebook-link" },
  { href: COMPANY.socials.instagram, Icon: Instagram, label: "Instagram", id: "footer-instagram-link" },
  { href: COMPANY.socials.linkedin, Icon: Linkedin, label: "LinkedIn", id: "footer-linkedin-link" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-primary text-white">
    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-5">
        <div className="flex items-center gap-4">
          <img src="/logo-icon.svg" alt="" className="h-12 w-12" />
          <p className="font-display text-2xl font-light tracking-tight">{COMPANY.legalName}</p>
        </div>
        <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-white/70">
          Independent solar operations &amp; maintenance. Certified service partner to leading manufacturers, delivering
          repair, warranty work, detach &amp; re-attach and technical quality control across Ohio and Northern Kentucky.
        </p>
        <div className="mt-8 flex items-center gap-3">
          {socials.map(({ href, Icon, label, id }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              data-testid={id}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/80 transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-primary"
            >
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </a>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        <p className="eyebrow text-accent">Navigate</p>
        <ul className="mt-5 space-y-3 text-sm font-light">
          <li><Link to="/" data-testid="footer-home-link" className="text-white/80 transition-colors hover:text-white">Home</Link></li>
          <li><Link to="/services" data-testid="footer-services-link" className="text-white/80 transition-colors hover:text-white">Services</Link></li>
          <li><Link to="/contact" data-testid="footer-contact-link" className="text-white/80 transition-colors hover:text-white">Contact Us</Link></li>
        </ul>
      </div>

      <div className="lg:col-span-2">
        <p className="eyebrow text-accent">Service Area</p>
        <ul className="mt-5 space-y-3 text-sm font-light text-white/80">
          {SERVICE_AREAS.map((a) => <li key={a}>{a}</li>)}
        </ul>
      </div>

      <div className="lg:col-span-3">
        <p className="eyebrow text-accent">Get in Touch</p>
        <ul className="mt-5 space-y-4 text-sm font-light">
          <li className="flex items-start gap-3 text-white/80">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} />
            <Link to="/contact" data-testid="footer-inquiry-link" className="transition-colors hover:text-white">Submit a service inquiry</Link>
          </li>
          <li className="flex items-start gap-3 text-white/80">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} />
            <span>Based in {COMPANY.location}</span>
          </li>
          <li>
            <a
              href={COMPANY.bbbUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-bbb-link"
              className="inline-flex items-center gap-2 rounded-full border border-accent/60 px-4 py-2 text-xs font-medium tracking-wide text-accent transition-[background-color,color] duration-200 hover:bg-accent hover:text-primary"
            >
              <ShieldCheck className="h-4 w-4" strokeWidth={1.75} /> BBB Accredited Business
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-6 text-xs text-white/50 sm:flex-row sm:items-center sm:justify-between lg:px-10">
        <span>&copy; {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.</span>
        <span>NABCEP Licensed &middot; BBB Accredited since June 2025</span>
      </div>
    </div>
  </footer>
);
